import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";

import { getCsrfToken } from "../api/userAuthApi";
import { loginDonor } from "../api/donorAuthApi";
import {
  clearAuthSession,
  getPostLoginRedirectPath,
  persistUserSession,
  resolveRoleFromLoginResponse,
} from "../utils/authSession";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

import GlobalFeedback from "../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../global/GlobalSuccessFlashModal";

import {
  GlobalEmailField,
  GlobalPasswordField,
} from "../../global/forms/GlobalFormFields";

import "../css/UserLogin.css";

/** Match backend donor auth checks */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;

function validateLoginFields({ email, password }) {
  const trimmedEmail = (email ?? "").trim().toLowerCase();
  const trimmedPassword = (password ?? "").trim();

  const fieldErrors = { email: "", password: "" };

  if (!trimmedEmail) {
    fieldErrors.email = "Email is required.";
  } else if (!EMAIL_REGEX.test(trimmedEmail)) {
    fieldErrors.email = "Enter a valid email address.";
  }

  if (!trimmedPassword) {
    fieldErrors.password = "Password is required.";
  } else if (!PASSWORD_REGEX.test(trimmedPassword)) {
    fieldErrors.password =
      "Use at least 8 characters with upper & lower case, a number, and one of: @ $ ! % * ? &";
  }

  const hasFieldError = !!(fieldErrors.email || fieldErrors.password);
  return {
    ok: !hasFieldError,
    fieldErrors,
    email: trimmedEmail,
    password: trimmedPassword,
    formError: hasFieldError ? "Please update the required fields below." : "",
  };
}

const DonorLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const pendingRedirectRef = useRef(null);

  const [fieldErrors, setFieldErrors] = useState({
    email: "",
    password: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");

    const check = validateLoginFields(formData);
    setFieldErrors(check.fieldErrors);
    if (!check.ok) {
      if (check.formError) setError(check.formError);
      return;
    }

    setLoading(true);

    try {
      clearAuthSession();
      const csrfToken = await getCsrfToken();

      const payload = {
        email: check.email,
        password: check.password,
      };

      const response = await loginDonor(payload, csrfToken);
      const data = await response.json();

      if (response.ok) {
        const token = data.token;

        if (!token) {
          setFieldErrors({ email: "", password: "" });
          setError("Login succeeded but token was missing. Please try again.");
          return;
        }
        persistUserSession({ token, csrfToken, data });

        setSuccess("Login successful!");
        setFormData({ email: "", password: "" });

        const resolvedRole = resolveRoleFromLoginResponse(data);
        pendingRedirectRef.current = getPostLoginRedirectPath(resolvedRole);
      } else {
        setFieldErrors({ email: "", password: "" });
        setError(data.message || "Login failed");
      }
    } catch (error) {
      setFieldErrors({ email: "", password: "" });
      setError("Network error. Please try again.");
      console.error("Donor login error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Navbar />
      <main className="login-main">
        <div className="login-wrap card-reveal">
          <div className="login-panel">
            <span className="login-panel-badge">Donor</span>
            <h1 className="login-panel-title">Giri Nestham</h1>
            <p className="login-panel-desc">Sign in to manage your donations.</p>
            <div className="login-panel-dot" aria-hidden="true" />
          </div>

          <div className="login-form-panel">
            <div className="login-form-panel-inner">
              <div className="login-form-header">
                <div>
                  <h2 className="login-form-title">Sign in</h2>
                  <p className="login-form-subtitle">
                    Use your donor credentials to continue.
                  </p>
                </div>
                <Link to="/" className="login-back">
                  ← Back to site
                </Link>
              </div>

              <GlobalFeedback
                type="error"
                text={error}
                role="alert"
                ariaLive="assertive"
                className="login-msg error"
              />

              <GlobalSuccessFlashModal
                message={success}
                onDismiss={() => {
                  setSuccess("");
                  const path = pendingRedirectRef.current;
                  pendingRedirectRef.current = null;
                  if (path) navigate(path);
                }}
              />

              <form className="login-form" onSubmit={handleSubmit} noValidate>
                <GlobalEmailField
                  id="donor-login-email"
                  label="Email"
                  value={formData.email}
                  onChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      email: value,
                    }));
                    setFieldErrors((prev) => ({ ...prev, email: "" }));
                  }}
                  error={fieldErrors.email}
                  placeholder="you@example.com"
                  autoComplete="email"
                  classNames={{
                    fieldWrap: "form-group",
                    error: "login-field-error",
                    inputError: "login-input--error",
                  }}
                />

                <GlobalPasswordField
                  id="donor-login-password"
                  label="Password"
                  value={formData.password}
                  onChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      password: value,
                    }));
                    setFieldErrors((prev) => ({ ...prev, password: "" }));
                  }}
                  error={fieldErrors.password}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  classNames={{
                    fieldWrap: "form-group",
                    error: "login-field-error",
                    inputError: "login-input--error",
                    passwordWrap: "login-password-wrap",
                    passwordToggle: "login-password-toggle",
                  }}
                  visibleLabel={{ show: "Show password", hide: "Hide password" }}
                />

                <div className="login-actions">
                  <button type="submit" className="login-btn" disabled={loading}>
                    {loading ? "Signing in…" : "Sign in"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
      <Footer organizationName="Giri Nestham" />
    </div>
  );
};

export default DonorLogin;

